import { useCases } from "./useCases";
import { solutions } from "./solutions";

export interface PageMetadata {
  path: string;
  title: string;
  description: string;
  noindex?: boolean;
}
export const pageMetadata: PageMetadata[] = [
  {
    path: "/",
    title: "Adaptive AI support for everyday life",
    description:
      "AI support that stays with people between the moments that matter. Explore Systein for everyday functioning, sustainable habits, and support beyond appointments.",
  },
  {
    path: "/solutions/individuals",
    title: "Support that fits everyday life",
    description:
      "Explore personal support for getting started, building routines, and following through. Meet Sara and Irene, specialized Systein experiences on WhatsApp.",
  },
  ...solutions.map((s) => ({
    path: `/solutions/${s.slug}`,
    title: `${s.title} — ${s.headline}`,
    description: s.description,
  })),
  {
    path: "/use-cases",
    title: "Everyday support use cases",
    description:
      "Explore support for executive functioning, stress, sleep, healthy habits, student life, neurodivergent routines, and care continuity.",
  },
  ...useCases.map((c) => ({
    path: `/use-cases/${c.slug}`,
    title: c.title,
    description: c.description,
  })),
  {
    path: "/platform",
    title: "How the Systein platform works",
    description:
      "Understand, adapt, support, learn, and connect. Explore the Systein approach to contextual AI companions and continuous everyday support.",
  },
  {
    path: "/platform/personalization",
    title: "Personalization that fits the moment",
    description:
      "See how goals, preferences, timing, and everyday context can shape a more useful next step. Explore practical personalization examples.",
  },
  {
    path: "/platform/messaging-voice",
    title: "Messaging, voice & proactive support",
    description:
      "Explore Systein messaging, agreed check-ins, and voice experiences. See how support can come to a person through a familiar conversation.",
  },
  {
    path: "/platform/integrations",
    title: "Integrations & connected context",
    description:
      "Review existing Systein communication channels and proposed wearable, scheduling, health platform, and API connections that require scoping.",
  },
  {
    path: "/platform/safety",
    title: "Safety, privacy & human support",
    description:
      "Understand Systein’s AI boundaries, privacy approach, human responsibilities, and the questions to discuss before an organizational pilot.",
  },
  {
    path: "/evidence",
    title: "Pilots & Research",
    description:
      "Explore Systein’s evaluation approach and discuss a focused pilot. A transparent place for verified studies and findings as they become available.",
  },
  {
    path: "/resources",
    title: "Guides & resources",
    description:
      "Understand adaptive support, personalization, care continuity, and responsible AI through Systein’s product guides.",
  },
  {
    path: "/about",
    title: "About Systein",
    description:
      "Technology should support people when life actually happens. Learn why Systein is developing adaptive AI support beyond scheduled appointments.",
  },
  {
    path: "/contact",
    title: "Contact Systein & book a demo",
    description:
      "Discuss personal support, request a demo, or explore a pilot with Systein. Contact the team about healthcare, employers, education, or digital health.",
  },
  {
    path: "/sara",
    title: "Sara — Weight Management Companion",
    description:
      "Meet Sara, a specialized Systein experience for sustainable habits, food decisions, movement, and reflection. Start a conversation on WhatsApp.",
  },
  {
    path: "/irene",
    title: "Irene — Focus & Executive Function Companion",
    description:
      "Explore Irene for planning, adaptive reminders, and everyday follow-through. Review the existing plan and start a conversation on WhatsApp.",
  },
  {
    path: "/Policy",
    title: "Privacy policy",
    description:
      "Read the existing SYSTEIN Solutions privacy policy, information about personal data processing, and contact details for privacy requests.",
  },
  {
    path: "/Terms",
    title: "Terms & conditions",
    description:
      "Read the terms and conditions for SYSTEIN Solutions services and subscriptions.",
  },
  {
    path: "/Imprint",
    title: "Legal notice & company details",
    description:
      "SYSTEIN Solutions company information, registration details, and contact information in the Netherlands.",
  },
  {
    path: "/success",
    title: "Payment confirmation",
    description: "Return to your Systein experience after payment.",
    noindex: true,
  },
  {
    path: "/cancel",
    title: "Payment cancelled",
    description: "Return to Systein after cancelling checkout.",
    noindex: true,
  },
];

export function metadataFor(pathname: string): PageMetadata {
  const normalized = pathname.replace(/\/$/, "").toLowerCase() || "/";
  return (
    pageMetadata.find((page) => page.path.toLowerCase() === normalized) || {
      path: pathname,
      title: "Page not found",
      description:
        "This page could not be found. Explore Systein or contact our team.",
      noindex: true,
    }
  );
}
